import { localizeValue } from '@mnd-frontend/utils';
import { ticks } from 'd3-array';
import { axisLeft } from 'd3-axis';
import { select } from 'd3-selection';
import { memo, useEffect, useRef } from 'react';
import type { DataPoint, Scale, ScaleBand } from '../../../../types';
import { useEventContext, useSvgContext } from '../../../context';
import AxisStyle from './AxisStyle';

const Left = memo(
  ({
    scale,
    percent,
    x,
    tickPadding = 12,
    includeFirstTick = false,
    drawAxis,
  }: {
    scale: Scale;
    percent?: boolean;
    x: number;
    tickPadding?: number;
    drawAxis?: boolean;
    includeFirstTick?: boolean;
  }) => {
    const ref = useRef(null);
    const { updatePadding } = useEventContext();
    const { left } = useSvgContext();
    const allTicks = (ticks as any)(...scale.domain(), scale.tickCount);

    useEffect(() => {
      const axis = (axisLeft as any)(scale)
        .tickPadding(tickPadding)
        .tickSize(0)
        .tickValues(includeFirstTick ? allTicks : allTicks.slice(1))
        .tickFormat((value: any) =>
          percent ? Math.abs(value) + '%' : localizeValue(value, { precision: 0, abbrev: true }),
        );
      select(ref.current)
        .call(axis)
        .call((g: any) => !drawAxis && g.select('.domain').remove());
      const newLeft = (ref.current as any).getBoundingClientRect().width;
      if (left !== newLeft) updatePadding({ left: newLeft });
    }, [scale, allTicks, updatePadding, tickPadding, drawAxis, percent, includeFirstTick, left]);

    return <AxisStyle ref={ref} transform={`translate(${x}, 0)`} />;
  },
);

export const Band = memo(
  ({
    scale,
    x,
    data,
    tickPadding = 12,
  }: {
    scale: ScaleBand;
    x: number;
    data: DataPoint[];
    tickPadding?: number;
  }) => {
    const ref = useRef(null);
    const { updatePadding } = useEventContext();
    const totalSteps = data.length - 1;
    const domain = scale.domain();
    const allTicks = ticks(domain[0], domain[domain.length - 1], Math.max(1, totalSteps));

    useEffect(() => {
      const axis = axisLeft(scale)
        .tickPadding(tickPadding)
        .tickSize(0)
        .tickValues(allTicks)
        .tickFormat((d: any) => data[d].name);
      select(ref.current).call(axis as any);
      updatePadding({ left: (ref.current as any).getBoundingClientRect().width });
    }, [data, scale, allTicks, updatePadding, tickPadding]);

    return <AxisStyle ref={ref} transform={`translate(${x}, 0)`} />;
  },
);

export default Left;
